import { Link } from '@tanstack/react-router';
import { ExternalLink, LifeBuoy } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { Organization, SupportSessionStarted } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { adminAppUrl } from '@/shared/admin-app';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { useStartSupportSession } from '../hooks/use-support-session-mutations';
import { supportReasonSchema, type SupportReasonValues } from '../schemas/profile-schema';

/**
 * Opens the admin app in a new tab. The API set the support session's staff cookie on its own
 * origin when it answered, so the admin app signs in from that cookie; no token travels in the URL.
 */
function openAdminApp(url: string): boolean {
  const tab = window.open(url, '_blank', 'noopener');
  return tab !== null;
}

export interface StartSupportSessionDialogProps {
  organization: Organization;
}

/**
 * A 60-minute, read-only look inside a tenant's admin. The reason is required and recorded in
 * the organization's own audit log; the backend also requires two-factor on the operator.
 */
export function StartSupportSessionDialog({ organization }: StartSupportSessionDialogProps) {
  const { t } = useTranslation(['organizations', 'common']);
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState<SupportSessionStarted | null>(null);
  const available = organization.status === 'active';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setStarted(null);
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          disabled={!available}
          data-tour="platform.organizations.start-support"
        >
          <LifeBuoy aria-hidden />
          {t('organizations:support.open')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('organizations:support.title', { name: organization.name })}</DialogTitle>
          <DialogDescription>{t('organizations:support.description')}</DialogDescription>
        </DialogHeader>
        {started ? (
          <Started started={started} onClose={() => setOpen(false)} />
        ) : open ? (
          <ReasonForm
            organizationId={organization.id}
            onStarted={setStarted}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface ReasonFormProps {
  organizationId: string;
  onStarted: (started: SupportSessionStarted) => void;
  onCancel: () => void;
}

function ReasonForm({ organizationId, onStarted, onCancel }: ReasonFormProps) {
  const { t } = useTranslation(['organizations', 'common']);
  const start = useStartSupportSession(organizationId);
  const target = adminAppUrl();
  const form = useForm<SupportReasonValues>({
    resolver: useI18nResolver(supportReasonSchema),
    defaultValues: { reason: '' },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);

  const onSubmit = form.handleSubmit(async ({ reason }) => {
    try {
      const started = await start.mutateAsync(reason);
      if (target) openAdminApp(target);
      onStarted(started);
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  const needsTwoFactor = start.error?.code === 'TWO_FACTOR_REQUIRED';

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {target === null ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{t('organizations:support.noAdminUrl')}</AlertDescription>
          </Alert>
        ) : null}
        {needsTwoFactor ? (
          <Alert variant="destructive" role="alert">
            <AlertTitle>{t('organizations:support.twoFactorTitle')}</AlertTitle>
            <AlertDescription>
              <span>
                {t('organizations:support.twoFactorBody')}{' '}
                <Link to="/account/security" className="font-medium underline">
                  {t('organizations:support.twoFactorLink')}
                </Link>
              </span>
            </AlertDescription>
          </Alert>
        ) : start.isError && !start.error.hasFieldErrors ? (
          <ServerError error={start.error} />
        ) : null}
        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('organizations:support.reason')}</FormLabel>
              <FormControl>
                <Textarea
                  autoFocus
                  rows={3}
                  placeholder={t('organizations:support.reasonPlaceholder')}
                  {...field}
                />
              </FormControl>
              <FormDescription>{t('organizations:support.reasonHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={start.isPending || target === null}>
            {start.isPending
              ? t('organizations:support.starting')
              : t('organizations:support.start')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

function Started({ started, onClose }: { started: SupportSessionStarted; onClose: () => void }) {
  const { t } = useTranslation(['organizations', 'common']);
  const format = useFormat();
  const target = adminAppUrl();
  return (
    <div className="flex flex-col gap-4">
      <Alert role="status">
        <LifeBuoy aria-hidden />
        <AlertTitle>{t('organizations:support.startedTitle')}</AlertTitle>
        <AlertDescription>
          {t('organizations:support.startedBody', {
            time: format.time(started.supportSession.expiresAt),
          })}
        </AlertDescription>
      </Alert>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          {t('common:actions.close')}
        </Button>
        {target ? (
          <Button asChild>
            <a href={target} target="_blank" rel="noopener noreferrer">
              <ExternalLink aria-hidden />
              {t('organizations:support.openAdmin')}
            </a>
          </Button>
        ) : null}
      </DialogFooter>
    </div>
  );
}
